<template>
  <form class="vl-form" @submit.prevent="onSubmit()">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('productprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('productprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-vl-spacer:bottom.small v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="duplicateDeliveryProcessDefinition"
            :label="$t('productprocessdefinition.actions.duplicate.duplicateDeliveryProcessDefinition')"
          />
        </vl-form-column>
        <vl-form-column
          v-if="duplicateDeliveryProcessDefinitionChecked"
          v-vl-spacer:bottom.small
          v-vl-spacer:top.small
          class="indented"
        >
          <PbsRadioButtonField
            name="duplicateDeliveryQualityChecks"
            :label="$t('productprocessdefinition.actions.duplicate.duplicateDeliveryQualityChecks')"
          />
        </vl-form-column>

        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{
            $t('productprocessdefinition.actions.duplicate.label')
          }}</vl-button>
          <vl-button
            v-focus
            :mod-disabled="isSaving"
            mod-icon-before
            mod-secondary
            tag-name="a"
            @click.prevent="cancel"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import { boolean, object } from 'yup';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import { parseException } from '@/modules/core/store/alert.store';

const router = useRouter();
const detailStore = useProductProcessDefinitionDetailStore();
const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  duplicateDeliveryProcessDefinition: boolean(),
  duplicateDeliveryQualityChecks: boolean()
});

const { handleSubmit, resetForm, values, setFieldValue } = useForm({ validationSchema });

const data = reactive({
  definition: null,
  isLoading: false,
  isSaving: false,
  error: null
});

const duplicateDeliveryProcessDefinitionChecked = computed(() => values.duplicateDeliveryProcessDefinition);

watch(
  () => values.duplicateDeliveryProcessDefinition,
  (newValue) => {
    if (values.duplicateDeliveryQualityChecks === undefined) {
      setFieldValue('duplicateDeliveryQualityChecks', false);
    }
  }
);

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadProductProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  setInitialFormValues();
});

const setInitialFormValues = () => {
  resetForm({
    values: {
      duplicateDeliveryProcessDefinition: false,
      duplicateDeliveryQualityChecks: false
    }
  });
};

const loadProductProcessDefinition = async () => {
  const productProcessDefinition = await detailStore.getDetail(props.id);
  if (productProcessDefinition) {
    data.definition = productProcessDefinition;
  }
};

const definition = computed(() => {
  return data.definition;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const error = computed(() => {
  return data.error;
});

const duplicate = async ({
  duplicateDeliveryProcessDefinition,
  duplicateDeliveryQualityChecks
}: {
  duplicateDeliveryProcessDefinition: boolean;
  duplicateDeliveryQualityChecks: boolean;
}): Promise<number> => {
  let id = null;
  try {
    data.isSaving = true;
    id = await detailStore.duplicate(props.id, {
      duplicateDeliveryProcessDefinition,
      duplicateDeliveryQualityChecks
    });
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return id;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onSuccess = async (values) => {
  const id = await duplicate({
    duplicateDeliveryProcessDefinition: values.duplicateDeliveryProcessDefinition,
    duplicateDeliveryQualityChecks: values.duplicateDeliveryProcessDefinition
      ? values.duplicateDeliveryQualityChecks
      : false
  });

  closeModal();

  if (id) {
    router.push({
      name: 'productprocessdefinition.detail',
      params: {
        id
      }
    });
  }
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped>
.indented {
  padding-left: 2rem;
}
</style>
